import { Close, Done } from '@mui/icons-material'
import { Box, Divider, MobileStepper, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material'
import React, { FunctionComponent, useEffect, useState } from 'react'
import Step1 from './step1'
import Step2 from './step2'
import Step3 from './step3'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { TanCreateReq } from '@/services/mobi-api/sim/types'
import { useSearchParams } from 'next/navigation'
import Step4 from './step4'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import useIntl from '@/utils/intl'
import Image from 'next/image'
import classNames from 'classnames'
import { IMG_URL } from '@/utils/constants'
import { useAuth } from '@/context/AuthContext'
// import Disabled from '@/pages/newnumberv2/disabled'

interface Props {
  onClose: () => void
}

const Sim: FunctionComponent<Props> = ({ onClose }) => {
  const [currentStep, setCurrentStep] = React.useState(0)
  const [payload, setPayload] = useState<TanCreateReq | undefined>()
  const searchParams = useSearchParams()
  const { isWebView } = useAuth()
  const { translate } = useIntl()

  const steps = [
    {
      label: translate('phoneNumber.insert'),
      description: translate('sim.selectType')
    },
    {
      label: translate('sim.verification'),
      description: ''
    },
    {
      label: translate('email.verification'),
      description: ''
    },
    {
      label: translate('payment')
    }
  ]
  useEffect(() => {
    handleParams()
  }, [searchParams])

  const handleParams = (): void => {
    if (searchParams.get('_id') !== null) {
      setCurrentStep(3)
    }
  }

  const handleNext = (req?: TanCreateReq): void => {
    const mergedPayload = {
      ...payload,
      ...req
    }
    setPayload(mergedPayload)
    setCurrentStep(currentStep + 1)
  }

  const handleClickStep = (idx: number): void => {
    if (idx < currentStep) {
      setCurrentStep(idx)
    }
  }

  const exitWebview = (): void => {
    let webView
    if ((window as any).mobiBridge !== undefined) {
      webView = (window as any).mobiBridge
    }
    if ((window as any).ReactNativeWebview !== undefined) {
      webView = (window as any).ReactNativeWebview
    }
    if (webView !== undefined) {
      const data = { key: 'exit' }
      webView.postMessage(JSON.stringify(data))
    }
  }

  const stepIcon = (index: number): any => {
    return (
      index === currentStep
        ? (
          <div className='bg-secondary p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-textInverse font-extrabold text-xs md:text-sm'>{index + 1}</span>
          </div>)
        : index <= (currentStep ?? 0)
          ? (
            <div className='bg-[#F0FDF4] border border-[#16A34A] p-2 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
              <Done className='text-[#16A34A] text-xs md:text-sm font-bold' />
            </div>)
          : (
            <div className='bg-[#231F1F1A] p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
              <span className='text-[#B0B0B0] font-extrabold text-xs md:text-sm'>{index + 1}</span>
            </div>)
    )
  }
  return (
    <div className='flex md:flex-row flex-col h-[90vh]'>
      <div className='bg-surfaceInverse flex flex-col xl:w-1/4 lg:w-2/5'>
        <div className='lg:p-14 md:p-8 p-4'>
          <div className='md:flex flex-col items-start hidden'>
            <Image draggable={false} width={50} height={50} src={IMG_URL + '/uploads/mobicom-logo-hq.png'} alt='logo' className='w-16' />
            <div>
              <h1 className='lg:text-2xl md:text-xl font-bold text-textHigh'>{translate('quick.simRecover')}</h1>
              <span className='text-textMid text-xs 2xl:text-sm font-medium'>{translate('simple')}</span>
            </div>
          </div>
          <div className='md:hidden flex justify-between items-center pb-2'>
            <div className='flex items-center'>
              <h1 className='font-bold text-sm text-textHigh border-r pr-2 mr-1'>{translate('quick.simRecover')}</h1>
              <MobileStepper
                className='bg-surfaceInverse'
                color='error'
                variant='dots'
                sx={{
                  '.MuiMobileStepper-dotActive': {
                    backgroundColor: 'red'
                  }
                }}
                steps={steps.length}
                position='static'
                activeStep={currentStep}
                backButton={<></>}
                nextButton={<></>}
              />
              <div className={classNames('', currentStep === 0 ? 'invisible' : 'visible')}>
                <DarkButton type='soft' fullwidth size='sm' onClick={() => setCurrentStep(currentStep - 1)}> {translate('back')}</DarkButton>
              </div>
            </div>
            <DarkIconButton type='soft' size='sm' icon={<Close />} onClick={isWebView ? exitWebview : onClose} />
          </div>
        </div>
        <Divider className='hidden md:flex' />
        <div className='lg:p-14 md:p-8 hidden md:flex'>
          <Stepper orientation='vertical' activeStep={currentStep}>
            {steps.map((step, index) => (
              <Step key={index} onClick={() => { handleClickStep(index) }}>
                <StepLabel StepIconComponent={() => stepIcon(index)}><Typography className='text-sm font-bold text-textHigh'>{step.label}</Typography></StepLabel>
                <StepContent>
                  <Typography className='text-xs text-textMid font-medium'>{step.description}</Typography>
                  {
                    index > 0
                      ? (
                        <Box sx={{ mt: 2 }}>
                          <DarkButton
                            type='soft'
                            size='sm'
                            onClick={() => setCurrentStep(index - 1)}
                          >
                            {translate('back')}
                          </DarkButton>
                        </Box>
                        )
                      : <></>
                  }
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </div>
      </div>
      <Divider orientation='vertical' flexItem />
      <div className='bg-surfaceWebGrey lg:p-6 md:p-3 w-full h-full'>
        <div className='flex justify-end w-full'>
          <div className='hidden md:flex'>
            <DarkIconButton type='soft' size='sm' icon={<Close />} onClick={onClose} />
          </div>
        </div>
        <div className='flex flex-col justify-center items-center md:-mt-3'>
          {currentStep === 0
            ? (
              <Step1 onClick={handleNext} />
              )
            : currentStep === 1
              ? (
                <Step2 onClick={handleNext} simtype={payload?.simtype} />
                )
              : currentStep === 2
                ? (
                  <Step3 onClick={handleNext} payload={payload} />
                  )
                : (
                  <Step4 />
                  )}
          {/* <Disabled /> */}
        </div>
      </div>
    </div>
  )
}
export default Sim
